import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import CuratorBadge from "../components/CuratorBadge";
import { Twitter, Copy, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const MAP = {
  boggy: {
    name: "Boggy", emoji: "🐸", code: "BOGGYSTYLE10",
    accent: "bg-green", accentText: "text-ink",
    headline: "I'm a 🐸 Boggy",
    blurb: "Loud, bright, and unbothered. I wear color like it owes me money.",
    cta: "Shop Boggy's picks", href: "/boggy",
  },
  fox: {
    name: "Fox", emoji: "🦊", code: "FOXSTYLE10",
    accent: "bg-orange", accentText: "text-white",
    headline: "I'm a 🦊 Fox",
    blurb: "Clean, considered, and perfectly cut. One great piece beats ten okay ones.",
    cta: "Shop Fox's picks", href: "/fox",
  },
};

export default function QuizResultPage() {
  const { who } = useParams();
  const c = MAP[who];
  const [mascots, setMascots] = useState({});
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!c) return;
    document.title = `${c.headline} at Boggy and Fox`;
    api.get("/mascots").then((r) => setMascots(r.data || {})).catch(() => {});
  }, [who, c]);

  if (!c) return <div className="p-10 font-display text-3xl uppercase">Not found.</div>;

  const portrait = who === "boggy" ? mascots.boggy : mascots.fox;
  const shareUrl = `${window.location.origin}/api/share/${who}`;

  const tweet = () => {
    const text = encodeURIComponent(`${c.headline} at Boggy and Fox. What are you?`);
    const url = encodeURIComponent(shareUrl);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "_blank");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied · paste in your IG/Stories");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div data-testid={`quiz-result-page-${who}`}>
      <section className={`${c.accent} ${c.accentText} border-b-4 border-[#1A1A1A]`}>
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 order-2 md:order-1">
            <div className="flex items-center gap-3 mb-3">
              <CuratorBadge curator={who} size="lg" />
              <span className="tag-sticker bg-paper text-ink text-lg rotate-tilt-l">style result</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.9]">
              {c.headline}
            </h1>
            <p className="mt-4 text-lg md:text-xl">{c.blurb}</p>

            <div className="mt-6 brut p-4 bg-paper text-ink">
              <div className="text-xs font-bold uppercase tracking-wider opacity-70">Your code · 10% off $25+</div>
              <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
                <span className="font-display text-2xl sm:text-3xl bg-yellow border-[3px] border-[#1A1A1A] px-3 py-1" data-testid="result-code">{c.code}</span>
                <Link to={c.href} className={`brut-btn ${c.accent} ${c.accentText} px-5 py-3 flex items-center gap-2`} data-testid="result-go-shop">
                  {c.cta} <ArrowRight size={18} strokeWidth={3} />
                </Link>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <button onClick={tweet} className="brut-btn bg-paper text-ink px-4 py-3 flex items-center gap-2" data-testid="share-tweet">
                <Twitter size={18} strokeWidth={3} /> Share on X / Twitter
              </button>
              <button onClick={copyLink} className="brut-btn bg-paper text-ink px-4 py-3 flex items-center gap-2" data-testid="share-copy">
                <Copy size={18} strokeWidth={3} /> {copied ? "Copied!" : "Copy share link"}
              </button>
            </div>
          </div>
          <div className="md:col-span-5 order-1 md:order-2 relative">
            <div className="brut bg-paper p-3 -rotate-2">
              {portrait ? (
                <img src={fileUrl(portrait)} alt={c.name} className="w-full h-auto" />
              ) : (
                <div className="aspect-square bg-yellow flex items-center justify-center font-display text-6xl uppercase">{c.name}</div>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t-4 border-[#1A1A1A] bg-paper py-6">
        <div className="max-w-4xl mx-auto px-4 md:px-8 flex flex-wrap gap-3 justify-between items-center text-sm">
          <Link to="/" className="font-display text-xl uppercase">← Back to Boggy <span className="font-sticker text-orange">&</span> Fox</Link>
          <Link to={`/quiz/${who === "boggy" ? "fox" : "boggy"}`} className="font-bold underline">See the {who === "boggy" ? "Fox" : "Boggy"} result →</Link>
        </div>
      </footer>
    </div>
  );
}
